import { useEffect, useMemo, useRef, useState } from "react";
import rotateImage from "../../@utils/rotateImage";

const EMPTY_DATA = [];

export const isValidPosition = (position) =>
  position && Number.isFinite(position.lat) && Number.isFinite(position.lng) &&
  Math.abs(position.lat) <= 90 && Math.abs(position.lng) <= 180;

// Cache pending work too: many vehicles share the same status and heading.
export const createIconCache = (rotate, limit = 512) => {
  const cache = new Map();
  return (source, direction) => {
    const value = Number(direction);
    const heading = Number.isFinite(value) ? ((value % 360) + 360) % 360 : 0;
    const key = `${source}:${heading}`;
    if (cache.has(key)) {
      const result = cache.get(key);
      cache.delete(key);
      cache.set(key, result);
      return result;
    }
    const result = Promise.resolve().then(() => rotate(source, heading)).catch(() => {
      if (cache.get(key) === result) cache.delete(key);
      return null; // The map uses its default icon for a failed rotation.
    });
    cache.set(key, result);
    if (cache.size > limit) cache.delete(cache.keys().next().value);
    return result;
  };
};

const cachedIcon = createIconCache(rotateImage);

// Defined once per Maps API, rather than once per component render.
let LabelOverlay;
let overlayBase;
const getLabelOverlay = () => {
  if (overlayBase === window.google.maps.OverlayView) return LabelOverlay;
  overlayBase = window.google.maps.OverlayView;
  LabelOverlay = class extends overlayBase {
    constructor(item) {
      super();
      this.update(item);
    }
    update(item) {
      const text = `${item.vehiclename}(${item.PlateNumber})`;
      if (this.position?.lat === item.markerPosition.lat &&
          this.position?.lng === item.markerPosition.lng && this.text === text) return;
      this.position = item.markerPosition;
      this.text = text;
      if (this.div) {
        if (this.div.textContent !== this.text) this.div.textContent = this.text;
        this.draw();
      }
    }
    onAdd() {
      this.div = document.createElement("div");
      Object.assign(this.div.style, {
        position: "absolute", padding: "9px", marginTop: "10px",
        backgroundColor: "rgba(30, 30, 30, 60%)",
        border: "1px solid rgba(30, 30, 30, 60%)", borderRadius: "3px",
        fontSize: "15px", fontWeight: "bold", color: "white",
        whiteSpace: "nowrap", zIndex: "1", pointerEvents: "none",
        transform: "translate(-50%, calc(100% - 5px))",
      });
      this.div.textContent = this.text;
      this.getPanes()?.overlayLayer?.appendChild(this.div);
    }
    draw() {
      if (!this.div) return;
      const pixel = this.getProjection()?.fromLatLngToDivPixel(
        new window.google.maps.LatLng(this.position.lat, this.position.lng)
      );
      if (!pixel) return;
      this.div.style.left = `${pixel.x}px`;
      this.div.style.top = `${pixel.y}px`;
    }
    onRemove() {
      this.div?.remove();
      this.div = null;
    }
  };
  return LabelOverlay;
};

export default function useTrackingMap({ enabled, data, map, labels, iconSource, zoom }) {
  const vehicles = useMemo(() => enabled
    ? (data || EMPTY_DATA).filter((item) => isValidPosition(item.markerPosition))
    : EMPTY_DATA, [enabled, data]);
  const overlays = useRef(new Map());
  const [icons, setIcons] = useState({});
  const fitted = useRef(false);
  const interacted = useRef(false);
  const fitCleanup = useRef(null);

  useEffect(() => {
    if (!enabled || !map) return;
    fitted.current = false;
    interacted.current = false;
    // Once fitted, later viewport changes (including wheel/pinch) are retained.
    const onInteraction = () => { interacted.current = true; };
    const listeners = [
      map.addListener("dragstart", onInteraction),
      map.addListener("zoom_changed", onInteraction),
    ];
    const currentOverlays = overlays.current;
    return () => {
      listeners.forEach((listener) => listener.remove());
      fitCleanup.current?.();
      fitCleanup.current = null;
      currentOverlays.forEach((overlay) => overlay.setMap(null));
      currentOverlays.clear();
    };
  }, [enabled, map]);

  useEffect(() => {
    if (!enabled || !map) return;
    const current = overlays.current;
    const ids = new Set(labels ? vehicles.map((item) => item.id) : []);
    current.forEach((overlay, id) => {
      if (!ids.has(id)) {
        overlay.setMap(null);
        current.delete(id);
      }
    });
    if (labels) {
      const Overlay = getLabelOverlay();
      vehicles.forEach((item) => {
        if (current.has(item.id)) current.get(item.id).update(item);
        else {
          const overlay = new Overlay(item);
          current.set(item.id, overlay);
          overlay.setMap(map);
        }
      });
    }
    if (!fitted.current && !interacted.current && vehicles.length) {
      const bounds = new window.google.maps.LatLngBounds();
      vehicles.forEach((item) => bounds.extend(item.markerPosition));
      fitted.current = true;
      if (vehicles.length === 1) {
        map.setCenter(vehicles[0].markerPosition);
        map.setZoom(zoom + 4);
      } else {
        // Temporarily cap initial fitting of clustered vehicles. Restore normal
        // zoom controls on idle, and also restore them if the page is closed early.
        const maxZoom = map.get("maxZoom");
        map.setOptions({ maxZoom: Math.min(maxZoom ?? Infinity, zoom + 4) });
        const idle = map.addListener("idle", () => {
          fitCleanup.current?.();
          fitCleanup.current = null;
        });
        fitCleanup.current = () => {
          idle.remove();
          map.setOptions({ maxZoom: maxZoom ?? null });
        };
        map.fitBounds(bounds);
      }
    }
  }, [enabled, map, vehicles, labels, zoom]);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    Promise.all(vehicles.map(async (item) => [
      item.id, await cachedIcon(iconSource(item), item.direction),
    ])).then((entries) => {
      if (active) setIcons(Object.fromEntries(entries));
    });
    return () => { active = false; };
  }, [enabled, vehicles, iconSource]);

  return { vehicles, icons };
}
